import { useMemo } from 'react';
import type React from 'react';
import {
  SortableDragHandle,
  SortableTableRow,
  SortableTableWrapper,
} from 'components/SortableTable';
import { chain } from 'lodash';
import type {
  BlankValue,
  CheckboxValue,
  CommonValue,
} from 'types/reservationSettingsColumnValues.type';
import type { TField, TReservationTemplate, TType } from '@timeedit/types';
import { Button, TEInput } from '@timeedit/ui-components';
import { Table, Typography } from '@timeedit/ui-components/antd';
import type { DefaultOptionType } from '@timeedit/ui-components/antd/select/index';
import { CommonValueInput } from './CommonValueInput';
import './FieldsList.scss';

type Props = {
  types: Array<{ label: string; value: any }>;
  onChange: (newValue: any[]) => void;
  onFieldChange: (index: number, field: string, newValue: any) => void;
  onAddRow: () => void;
  values: Array<CommonValue | BlankValue | CheckboxValue>;
  reservationTemplate?: TReservationTemplate;
  allTypes: TType[];
  allFields: TField[];
  objectTypeIds?: (string | number)[];
  onRowSelect: (rows: any[]) => void;
  onRowDelete: () => void;
  disableWidth?: boolean;
  hasError: (key: string, index: number, type?: string) => boolean;
  extraOptions?: DefaultOptionType[];
  columnSet?: 'labelAndWidth';
  lockedType?: boolean;
  lockedTypeId?: string;
  extraColumns?: Array<{
    key: string;
    dataIndex: string;
    title: string | React.ReactNode;
    render: (value: any, _: any, index: any) => JSX.Element;
    width: string;
  }>;
};

const FieldsList = (props: Props) => {
  const sharedColumns = useMemo(
    () => [
      {
        key: 'type',
        dataIndex: 'type',
        title: 'Type',
        render: (value, _, index) => {
          const hasError = props.hasError('type', index);
          return (
            <TEInput.Select
              className="fields-list-full-width"
              allowClear={false}
              value={value}
              options={props.types}
              onChange={(val) => {
                props.onFieldChange(index, 'type', val);
                props.onFieldChange(
                  index,
                  'value',
                  ['blank', 'checkbox', 'seat'].includes(val) ? val : undefined
                );
              }}
              status={hasError ? 'error' : undefined}
            />
          );
        },
        width: '150px',
      },
      {
        key: 'value',
        dataIndex: 'value',
        title: 'Value',
        render: (_, record: CommonValue, index) => {
          const hasError = props.hasError('value', index, record.type);
          return (
            <CommonValueInput
              lockedTypeId={props.lockedTypeId}
              lockedType={props.lockedType}
              allFields={props.allFields}
              allTypes={props.allTypes}
              reservationTemplate={props.reservationTemplate}
              objectTypeIds={props.objectTypeIds}
              type={record}
              onChange={(val) => props.onFieldChange(index, 'value', val)}
              hasError={hasError}
              extraOptions={
                props.extraOptions ?? [
                  { label: 'Id', value: 'id' },
                  { label: 'Date', value: 'date' },
                  { label: 'Time', value: 'time' },
                  { label: 'Size', value: 'size' },
                ]
              }
            />
          );
        },
        width: '400px',
      },
    ],
    [
      props.onFieldChange,
      props.allFields,
      props.allTypes,
      props.reservationTemplate,
      props.hasError,
    ]
  );
  const columns = useMemo(() => {
    const columns: {
      key: string;
      width: string;
      dataIndex?: string;
      title?: string | React.ReactNode;
      render?: (value: any, _: any, index: any) => JSX.Element;
    }[] = [];

    columns.push({
      key: 'dragAndDrop',
      width: '35px',
      render: () => <SortableDragHandle />,
    });

    if (props.columnSet === 'labelAndWidth') {
      columns.push({
        key: 'label',
        dataIndex: 'label',
        title: 'Label',
        render: (_, record, index) => {
          const hasError = props.hasError('label', index);
          return (
            <TEInput.Input
              value={record.label}
              onChange={(newValue) =>
                props.onFieldChange(index, 'label', newValue)
              }
              status={hasError ? 'error' : undefined}
            />
          );
        },
        width: '200px',
      });
    }

    columns.push(...(props.extraColumns ?? []), ...sharedColumns);

    if (props.columnSet === 'labelAndWidth' && !props.disableWidth) {
      columns.push({
        key: 'width',
        dataIndex: 'width',
        title: 'Width',
        width: '150px',
        render: (value: number | string | null, _: any, index: number) => {
          const hasError = props.hasError('width', 0);
          return (
            <TEInput.InputNumber
              value={value}
              suffix="%"
              placeholder="Auto"
              max={100}
              min={0}
              status={hasError ? 'error' : undefined}
              onChange={(val) => {
                props.onFieldChange(
                  index,
                  'width',
                  val === 0 || Number.isNaN(val) ? null : val
                );
              }}
            />
          );
        },
      });
    }

    return columns;
  }, [
    props.onFieldChange,
    props.allFields,
    props.allTypes,
    props.reservationTemplate,
    props.hasError,
    sharedColumns,
    props.extraColumns,
  ]);

  const totalWidth = useMemo(
    () =>
      chain(props.values)
        .map((value) => ('width' in value ? value.width : 0))
        .sum()
        .value(),
    [props.values]
  );

  const footer = useMemo(
    () => (
      <div className="fields-list-footer-wrapper">
        <div className="fields-list-footer-buttons-wrapper">
          <Button type="primary" size="small" onClick={props.onAddRow}>
            New row
          </Button>
          <Button onClick={props.onRowDelete} size="small">
            Remove selected rows
          </Button>
        </div>
        {!props.disableWidth && (
          <Typography.Text>Total width: {totalWidth}%</Typography.Text>
        )}
      </div>
    ),
    [props.onRowDelete, props.onAddRow, totalWidth]
  );

  const rows = useMemo(
    () =>
      (props.values ?? []).map((v, i) => ({
        ...v,
        __sortableKey: v.id ?? `row-${i}`,
      })),
    [props.values]
  );

  return (
    <div className="fields-list-wrapper">
      <SortableTableWrapper
        items={rows}
        getId={(row) => row.__sortableKey}
        onReorder={(newRows) =>
          props.onChange(newRows.map(({ __sortableKey, ...rest }) => rest))
        }
      >
        <Table
          rowKey="__sortableKey"
          columns={columns}
          pagination={false}
          size="small"
          dataSource={rows}
          footer={() => footer}
          components={{ body: { row: SortableTableRow } }}
          rowSelection={{
            type: 'checkbox',
            onChange: (selectedKeys) =>
              props.onRowSelect(
                (props.values ?? []).filter((v, i) =>
                  selectedKeys.includes(v.id ?? `row-${i}`)
                )
              ),
          }}
        />
      </SortableTableWrapper>
    </div>
  );
};

export default FieldsList;
